import { BaseAudioSource } from "./audio_source_base.js";
import { decodeToMonoPcmAtRate } from "./audio_resampler.js";

/**
 * FileAudioSource
 *
 * - 使用 <audio> 當作播放器 UI（辨識中允許播放/暫停；seek 明確 fail closed）
 * - 以 16 kHz 的 OfflineAudioContext 解碼成 mono PCM（與 Mic Mode 同一顆重採樣引擎），
 *   再依 chunkSec 切塊
 * - chunk 跟著 audio.currentTime 單向送出；不把跳躍時間軸拼接成完整逐字稿
 */
export class FileAudioSource extends BaseAudioSource {
  constructor() {
    super();

    // config
    this._targetSampleRate = 16000;
    this._chunkSec = 0.1;

    // UI audio element
    this._audioEl = null;

    // decoded + processed PCM (mono, 16k)
    this._pcm16k = null;

    // scheduler
    this._timerId = null;
    this._lastSentSample = 0;
    this._sentSampleCount = 0;
    // server accepted watermark 的 client 端鏡像；seek 後不等於音檔播放 offset。
    this._acceptedSampleCount = 0;

    // web audio decode
    this._audioCtx = null;

    this._currentObjectUrl = null;
    this._sessionId = null;
    this._eosTimer = null;
    this._drainGeneration = 0;
    this._pendingDrain = null;
    this._canSendChunk = null;
    this._getTransportIdentity = null;
    this._waitForTransportDrain = null;
    this._waitForAcceptedSamples = null;
    this._sendAudioEnd = null;
    this._onDrainError = null;
    this._onSeekRejected = null;
    this._drainTimeoutMs = 8000;

    // 播放結束時要打的 EOS。原檔寫死 fetch("/eos")——那是 KKASR 自己 serve
    // dev.html 時才成立（同源）。在 kingcolon 裡頁面來自 client:9530、KKASR 在
    // 5010，相對路徑會打到 kingcolon server 變成 404，EOS 永遠送不出去、
    // LLM 也就永遠不會跑。改成由呼叫端注入一支 onEos(sessionId)。
    // 未注入時維持原本的相對路徑行為（KKASR 的 dev.html 不受影響）。
    this._onEos = null;

    // playback state
    this._isSeeking = false;
    this._seekRejected = false;
    this._programmaticSeekGeneration = 0;
    this._pendingProgrammaticSeek = null;
    this._activeProgrammaticSeekGeneration = null;

    // bind handlers
    this._onPlay = this._onPlay.bind(this);
    this._onPause = this._onPause.bind(this);
    this._onSeeking = this._onSeeking.bind(this);
    this._onSeeked = this._onSeeked.bind(this);
    this._onEnded = this._onEnded.bind(this);
  }

  /**
   * init
   * @param {Object} config
   * @param {HTMLAudioElement} config.audioElement - <audio> element (UI)
   * @param {number} [config.targetSampleRate=16000]
   * @param {number} [config.chunkSec=0.1]
   * @param {Function} [config.onEos] - function(sessionId)，播放結束時呼叫；
   *        未給則沿用原本的 fetch("/eos")（同源部署用）
   * @param {Function} config.canSendChunk - transport 是否仍可接受音訊
   * @param {Function} config.getTransportIdentity - 目前 WebSocket identity
   * @param {Function} config.waitForTransportDrain - 有界等待 socket buffer 排空
   * @param {Function} config.sendAudioEnd - 在同一 socket 送出最後 PCM 後的序列 marker
   * @param {Function} [config.onDrainError] - drain fail-closed 的可見錯誤
   */
  init(config = {}) {
    const {
      audioElement,
      targetSampleRate = 16000,
      chunkSec = 0.1,
      onEos = null,
      canSendChunk = null,
      getTransportIdentity = null,
      waitForTransportDrain = null,
      waitForAcceptedSamples = null,
      sendAudioEnd = null,
      onDrainError = null,
      onSeekRejected = null,
      drainTimeoutMs = 8000,
    } = config;

    if (!audioElement) {
      throw new Error("FileAudioSource.init requires { audioElement }");
    }

    this._audioEl = audioElement;
    this._targetSampleRate = targetSampleRate;
    this._chunkSec = chunkSec;
    this._onEos = typeof onEos === "function" ? onEos : null;
    this._canSendChunk = typeof canSendChunk === "function" ? canSendChunk : null;
    this._getTransportIdentity = typeof getTransportIdentity === "function"
      ? getTransportIdentity
      : null;
    this._waitForTransportDrain = typeof waitForTransportDrain === "function"
      ? waitForTransportDrain
      : null;
    this._waitForAcceptedSamples = typeof waitForAcceptedSamples === "function"
      ? waitForAcceptedSamples
      : null;
    this._sendAudioEnd = typeof sendAudioEnd === "function" ? sendAudioEnd : null;
    this._onDrainError = typeof onDrainError === "function" ? onDrainError : null;
    this._onSeekRejected = typeof onSeekRejected === "function" ? onSeekRejected : null;
    this._drainTimeoutMs = Math.max(1, Number(drainTimeoutMs) || 8000);

    // AudioContext 用於 decode（不一定要用來播放）
    this._audioCtx =
      this._audioCtx ||
      new (window.AudioContext || window.webkitAudioContext)();

    // 綁定 audio UI 事件
    this._audioEl.addEventListener("play", this._onPlay);
    this._audioEl.addEventListener("pause", this._onPause);
    this._audioEl.addEventListener("seeking", this._onSeeking);
    this._audioEl.addEventListener("seeked", this._onSeeked);
    this._audioEl.addEventListener("ended", this._onEnded);

    super.init(config);
  }

  /**
   * 載入檔案（必須呼叫）
   * @param {File} file
   */
  async loadFile(file) {
    if (!file) throw new Error("loadFile(file) requires a File object");
    if (!this._audioEl)
      throw new Error("init({audioElement}) must be called before loadFile()");

    this.cancelPendingEos();
    this._pendingProgrammaticSeek = null;
    this._activeProgrammaticSeekGeneration = null;
    this._programmaticSeekGeneration += 1;

    // revoke 舊的
    if (this._currentObjectUrl) {
      URL.revokeObjectURL(this._currentObjectUrl);
      this._currentObjectUrl = null;
    }

    const objectUrl = URL.createObjectURL(file);
    this._currentObjectUrl = objectUrl;

    this._audioEl.src = objectUrl;
    this._audioEl.load();

    // 🔑🔑🔑 等 metadata ready（關鍵）
    await new Promise((resolve) => {
      const onLoaded = () => {
        this._audioEl.removeEventListener("loadedmetadata", onLoaded);
        resolve();
      };
      this._audioEl.addEventListener("loadedmetadata", onLoaded);
    });

    // 之後再做 decode（順序很重要）
    //
    // 舊做法是「以裝置原生率（48 kHz）解碼 → 手寫 downsample() 抽點回 16 kHz」，
    // 等於來回失真兩次，而且那支抽點沒有抗混疊低通。改走與 Mic Mode 同一支
    // 重採樣模組：File Mode 量到的數字才對真麥克風有參考價值。
    const arrayBuffer = await file.arrayBuffer();
    const decodeResult = await decodeToMonoPcmAtRate(
      arrayBuffer,
      this._targetSampleRate,
      this._audioCtx
    );

    this._pcm16k = decodeResult.pcm;
    this._lastSentSample = 0;
    this._sentSampleCount = 0;
    this._acceptedSampleCount = 0;
    console.log("====== AUDIO DEBUG ======");
    console.log("Resample route:", decodeResult.route);
    console.log("Decoded sampleRate:", decodeResult.decodedSampleRate);
    console.log("Channels:", decodeResult.channelCount);

    console.log("HTMLAudio duration:", this._audioEl.duration);

    const pcmDur = decodeResult.pcm.length / this._targetSampleRate;
    console.log("PCM16k duration:", pcmDur);

    console.log("Duration diff:",
      pcmDur - this._audioEl.duration
    );
  }

  setSessionId(sessionId) {
    this._sessionId = sessionId || null;
  }

  getResumeOffsetSec() {
    if (!Number.isFinite(this._lastSentSample) || this._targetSampleRate <= 0) return 0;
    return Math.max(0, this._lastSentSample / this._targetSampleRate);
  }

  getAcceptedSampleCount() {
    return Number.isSafeInteger(this._acceptedSampleCount)
      ? Math.max(0, this._acceptedSampleCount)
      : 0;
  }

  /** F5 後重選同一音檔時從最後已送出的 sample 續送，避免漏音或重播。 */
  setPlaybackPosition(seconds) {
    if (!this._audioEl || !this._pcm16k) return false;
    const requested = Number(seconds);
    if (!Number.isFinite(requested) || requested < 0) return false;
    const duration = Number(this._audioEl.duration);
    const position = Number.isFinite(duration) ? Math.min(requested, duration) : requested;
    this._setProgrammaticPlaybackPosition(position);
    this._lastSentSample = Math.floor(position * this._targetSampleRate);
    return true;
  }

  _setProgrammaticPlaybackPosition(seconds) {
    const generation = ++this._programmaticSeekGeneration;
    const targetTime = Number(seconds);
    this._activeProgrammaticSeekGeneration = null;
    if (this._currentTimeMatches(targetTime)) {
      this._pendingProgrammaticSeek = null;
      return generation;
    }
    this._pendingProgrammaticSeek = { generation, targetTime };
    this._audioEl.currentTime = targetTime;
    return generation;
  }

  _currentTimeMatches(targetTime) {
    if (!this._audioEl) return false;
    const tolerance = Math.max(0.001, 1 / Math.max(1, this._targetSampleRate));
    return Math.abs(Number(this._audioEl.currentTime) - targetTime) <= tolerance;
  }

  _matchesPendingProgrammaticSeek() {
    const pending = this._pendingProgrammaticSeek;
    return Boolean(pending && this._currentTimeMatches(pending.targetTime));
  }

  /** F5 接續時以 server receipt 還原累積 accepted watermark。 */
  setAcceptedSampleCount(samples) {
    const accepted = Number(samples);
    if (!Number.isSafeInteger(accepted) || accepted < 0) return false;
    this._acceptedSampleCount = accepted;
    this._sentSampleCount = accepted;
    return true;
  }

  noteAudioAck(payload) {
    const accepted = Number(payload && payload.accepted_samples);
    if (!Number.isSafeInteger(accepted) || accepted < this._acceptedSampleCount) return false;
    this._acceptedSampleCount = accepted;
    return true;
  }

  cancelPendingEos() {
    if (this._eosTimer) {
      clearTimeout(this._eosTimer);
      this._eosTimer = null;
    }
    this._drainGeneration += 1;
  }



  /**
   * start:
   * - 不主動 play
   * - 僅允許在播放狀態下 emit chunk
   */
  async start() {
    this.cancelPendingEos();
    if (!this._audioEl)
      throw new Error("init({audioElement}) must be called before start()");
    if (!this._pcm16k)
      throw new Error("loadFile(file) must be called before start()");
    // 新 session 的一次播放固定從頭開始；只有 F5 server receipt 已還原時
    // 才能從非零 offset 接續，避免開始前 scrub 造成第一個 span 不是 0。
    if (this._acceptedSampleCount === 0 && this._lastSentSample !== 0) {
      this._setProgrammaticPlaybackPosition(0);
      this._lastSentSample = 0;
      this._sentSampleCount = 0;
    }
    // 瀏覽器會延後送 seeking/seeked；token 必須跨過 running transition。
    super.start();
  }

  /**
   * pause: 暫停 emit（不干涉 UI 播放狀態）
   */
  pause() {
    this.cancelPendingEos();
    super.pause();
    this._stopScheduler();
  }

  /**
   * stop: 停止 emit 並 reset 到開頭
   */
  stop() {
    this.cancelPendingEos();
    super.stop();

    if (this._audioEl) {
      this._audioEl.pause();
      this._setProgrammaticPlaybackPosition(0);
    }

    this._lastSentSample = 0;
    this._sentSampleCount = 0;
    this._stopScheduler();
  }

  /**
   * destroy: 解除事件、釋放資源
   */
  destroy() {
    this.cancelPendingEos();
    this._stopScheduler();

    if (this._audioEl) {
      this._audioEl.removeEventListener("play", this._onPlay);
      this._audioEl.removeEventListener("pause", this._onPause);
      this._audioEl.removeEventListener("seeking", this._onSeeking);
      this._audioEl.removeEventListener("seeked", this._onSeeked);
      this._audioEl.removeEventListener("ended", this._onEnded);
    }

    if (this._currentObjectUrl) {
      URL.revokeObjectURL(this._currentObjectUrl);
      this._currentObjectUrl = null;
    }

    this._pcm16k = null;
    this._pendingProgrammaticSeek = null;
    this._activeProgrammaticSeekGeneration = null;
    super.destroy();
  }

  // =========================
  // Internal Scheduler
  // =========================
  _startScheduler() {
    if (this._timerId) return;

    const chunkSize = Math.floor(this._targetSampleRate * this._chunkSec);
    const intervalMs = Math.max(10, Math.floor(this._chunkSec * 1000));

    // ✅ 用 wall-clock 做 pacing（禁止 catch-up）
    this._nextWallTime = performance.now();

    this._timerId = window.setInterval(() => {
      if (!this._pcm16k) return;
      if (this._isSeeking) return;
      if (this._audioEl.paused) return;

      const now = performance.now();

      // ✅ 沒到時間就不送（重要：避免一次送很多 chunk 追趕）
      if (now < this._nextWallTime) return;

      // ✅ 即使 timer 卡頓，也只往後推「一格」，不補送多格
      this._nextWallTime += intervalMs;

      // 防止極端卡頓導致 nextWallTime 落後太多，直接拉回來
      if (now - this._nextWallTime > 3 * intervalMs) {
        this._nextWallTime = now + intervalMs;
      }

      // ✅ 以播放時間計算「應該送到哪裡」
      const t = this._audioEl.currentTime;
      const shouldSentSample = Math.floor(t * this._targetSampleRate);

      // ✅ 不允許超前播放：最多只能送到 shouldSentSample
      //    （可以讓它稍微落後一點，這裡用 0.5 chunk 當安全緩衝）
      const maxAhead = Math.floor(chunkSize * 0.5);
      const maxSendPos = Math.max(0, shouldSentSample + maxAhead);

      // 如果我們已經送得太前面了，就暫停送（等播放追上）
      if (this._lastSentSample >= maxSendPos) return;

      const start = this._lastSentSample;
      const end = Math.min(start + chunkSize, this._pcm16k.length);

      if (start >= this._pcm16k.length) {
        this._stopScheduler();
        return;
      }

      // ⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐
      // NEW — 真正 realtime gate
      // ⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐⭐

      const chunkEndSec = end / this._targetSampleRate;
      const playerTime = this._audioEl.currentTime;

      // 不允許 chunk 超前播放器
      if (chunkEndSec > playerTime) {
        return;
      }

      const chunk = this._pcm16k.subarray(start, end);
      // ===== SIZE DEBUG =====
      const samples = chunk.length;
      const bytes = chunk.byteLength;
      // console.log(
      //   "SEND",
      //   "chunkEnd=", chunkEndSec.toFixed(3),
      //   "player=", playerTime.toFixed(3),
      //   "delta=", (chunkEndSec - playerTime).toFixed(3),
      //   `[CHUNK] samples=${samples} bytes=${bytes} ` +
      //   `sec=${(samples / this._targetSampleRate).toFixed(3)}`
      // );

      const accepted = this._emitChunk(chunk, { startSample: start, endSample: end });
      if (accepted !== true) return;
      this._lastSentSample = end;
      this._sentSampleCount += chunk.length;
    }, 5); // ✅ timer tick 用小一點，靠 pacing 控速
  }



  _stopScheduler() {
    console.log("_stopScheduler");
    if (this._timerId) {
      window.clearInterval(this._timerId);
      this._timerId = null;
    }
  }

  // =========================
  // Audio Element Event Handlers
  // =========================
  _onPlay() {
    const seeking = this._isSeeking;

    console.log(
      "[FileAudioSource::_onPlay]",
      "isSeeking =", seeking,
      "| timerId =", this._timerId
    );

    if (!seeking) {
      console.log("[FileAudioSource::_onPlay] start scheduler");
      this._startScheduler();
    }
  }



  _onPause() {
    this._stopScheduler();
  }

  _onSeeking() {
    this._isSeeking = true;
    this._stopScheduler();
    if (this._matchesPendingProgrammaticSeek()) {
      this._activeProgrammaticSeekGeneration = this._pendingProgrammaticSeek.generation;
      return;
    }
    // pending target 不同代表使用者在程式化定位尚未 settle 前又操作時間軸。
    this._pendingProgrammaticSeek = null;
    this._activeProgrammaticSeekGeneration = null;
    if (!this.isRunning()) return;
    // File span 只接受 0→N 或 F5 receipt→N；任意跳轉不能冒充完整音訊。
    this._seekRejected = true;
    this.cancelPendingEos();
    super.pause();
    if (this._audioEl && !this._audioEl.paused) this._audioEl.pause();
    if (this._onSeekRejected) {
      this._onSeekRejected("辨識期間不支援快轉或倒轉；本次音訊已停止，請重新開始收音");
    }
  }

  _onSeeked() {
    if (!this._pcm16k) return;

    if (this._seekRejected) {
      this._seekRejected = false;
      this._isSeeking = false;
      return;
    }

    if (
      this._matchesPendingProgrammaticSeek()
      && this._activeProgrammaticSeekGeneration
        === this._pendingProgrammaticSeek.generation
    ) {
      this._pendingProgrammaticSeek = null;
      this._activeProgrammaticSeekGeneration = null;
      this._isSeeking = false;
      if (this.isRunning() && !this._audioEl.paused) this._startScheduler();
      return;
    }
    this._pendingProgrammaticSeek = null;
    this._activeProgrammaticSeekGeneration = null;

    const t = this._audioEl.currentTime;
    if (!this.isRunning()) {
      this._lastSentSample = Math.floor(t * this._targetSampleRate);
    }

    this._isSeeking = false;

    if (this.isRunning() && !this._audioEl.paused) {
      this._startScheduler();
    }
  }

  _isDrainValid(generation, pcm, sessionId, transportIdentity) {
    if (generation !== this._drainGeneration) return false;
    if (!this.isInitialized() || !this.isRunning()) return false;
    if (!sessionId || !pcm || this._pcm16k !== pcm || this._sessionId !== sessionId) return false;
    if (!transportIdentity || typeof this._canSendChunk !== "function") return false;
    if (typeof this._getTransportIdentity !== "function") return false;
    try {
      return this._canSendChunk() === true
        && this._getTransportIdentity() === transportIdentity;
    } catch {
      return false;
    }
  }

  async _waitForDrainTransport(generation, pcm, sessionId, transportIdentity, deadline) {
    if (!this._isDrainValid(generation, pcm, sessionId, transportIdentity)) return false;
    if (typeof this._waitForTransportDrain !== "function") return false;
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) return false;
    try {
      const drained = await this._waitForTransportDrain({
        timeoutMs: remainingMs,
        pollMs: 20,
        isCancelled: () => !this._isDrainValid(
          generation,
          pcm,
          sessionId,
          transportIdentity
        ),
      });
      return drained === true
        && Date.now() < deadline
        && this._isDrainValid(generation, pcm, sessionId, transportIdentity);
    } catch {
      return false;
    }
  }

  _yieldDrainTurn() {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  async _waitForServerAcceptance(
    generation,
    pcm,
    sessionId,
    transportIdentity,
    deadline,
    targetSamples
  ) {
    if (typeof this._waitForAcceptedSamples !== "function") return false;
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) return false;
    const accepted = await this._waitForAcceptedSamples(targetSamples, {
      timeoutMs: remainingMs,
      pollMs: 20,
      isCancelled: () => !this._isDrainValid(
        generation, pcm, sessionId, transportIdentity
      ),
    });
    return accepted === true
      && this._acceptedSampleCount >= targetSamples
      && this._isDrainValid(generation, pcm, sessionId, transportIdentity);
  }

  _reportDrainFailure(message, generation) {
    // stop/destroy/load 等主動 lifecycle 切換不應被舊 promise 反向覆蓋 UI。
    if (generation !== this._drainGeneration) return;
    super.pause();
    if (this._onDrainError) this._onDrainError(message);
  }

  /**
   * `ended` 與 scheduler 是兩個時鐘。尾段只能逐 chunk 送入仍有效的 transport，
   * 每次等待瀏覽器 WebSocket queue 排空後才推進 offset；整段有共同 deadline，
   * stop／destroy／換檔／斷線／generation 切換都會取消且禁止 EOS。
   */
  async _drainUnsentPcmBeforeEos(generation, pcm, sessionId, transportIdentity) {
    if (!this._isDrainValid(generation, pcm, sessionId, transportIdentity)) return null;
    const deadline = Date.now() + this._drainTimeoutMs;
    const chunkSize = Math.max(1, Math.floor(this._targetSampleRate * this._chunkSec));
    let flushedChunks = 0;

    while (this._lastSentSample < pcm.length) {
      // 共同 deadline 是 send-before gate；上一輪 yield 後若已逾時，不能再把
      // 下一包塞進 socket，否則 offset 未確認時恢復會重送同一段音訊。
      if (Date.now() >= deadline) return null;
      if (!this._isDrainValid(generation, pcm, sessionId, transportIdentity)) return null;
      const start = this._lastSentSample;
      const end = Math.min(start + chunkSize, pcm.length);
      let accepted = false;
      try {
        accepted = await this._emitChunk(
          pcm.subarray(start, end),
          { startSample: start, endSample: end }
        );
      } catch {
        return null;
      }
      if (
        accepted !== true
        || !this._isDrainValid(generation, pcm, sessionId, transportIdentity)
      ) return null;
      if (!await this._waitForDrainTransport(
        generation,
        pcm,
        sessionId,
        transportIdentity,
        deadline
      )) return null;
      this._lastSentSample = end;
      this._sentSampleCount += end - start;
      if (!await this._waitForServerAcceptance(
        generation,
        pcm,
        sessionId,
        transportIdentity,
        deadline,
        this._sentSampleCount
      )) return null;
      flushedChunks += 1;
      // 即使測試 transport 立即回報 queue=0，大 backlog 也要讓出 event loop。
      await this._yieldDrainTurn();
    }

    // scheduler 送完的最後一包也可能仍在 socket queue；EOS 仍須等它排空。
    if (!await this._waitForDrainTransport(
      generation,
      pcm,
      sessionId,
      transportIdentity,
      deadline
    )) return null;
    if (!await this._waitForServerAcceptance(
      generation,
      pcm,
      sessionId,
      transportIdentity,
      deadline,
      this._sentSampleCount
    )) return null;
    return flushedChunks;
  }

  async _finishEnded(generation, pcm, sessionId, transportIdentity) {
    const flushedChunks = await this._drainUnsentPcmBeforeEos(
      generation,
      pcm,
      sessionId,
      transportIdentity
    );
    if (
      flushedChunks === null
      || !this._isDrainValid(generation, pcm, sessionId, transportIdentity)
    ) {
      this._reportDrainFailure("音檔尾端傳送未完成，已取消 EOS，請重新接續", generation);
      return false;
    }

    // 從最後一次 transport gate 到 HTTP EOS 之間不再 await，避免 lifecycle race。
    super.pause();
    const audioTarget = {
      sessionId,
      targetSamples: this._acceptedSampleCount,
      sampleRate: this._targetSampleRate,
    };
    if (!this._sendAudioEnd || this._sendAudioEnd(audioTarget) !== true) {
      this._reportDrainFailure("音檔收樣確認送出失敗，已取消 EOS，請重新接續", generation);
      return false;
    }
    console.log(
      "[FileAudioSource] playback ended — sending EOS",
      `flushedChunks=${flushedChunks}`
    );
    try {
      if (this._onEos) {
        await this._onEos(sessionId, audioTarget);
      } else {
        const params = new URLSearchParams({
          session_id: sessionId || "",
          target_samples: String(audioTarget.targetSamples),
          sample_rate: String(audioTarget.sampleRate),
        });
        await fetch(`/eos?${params.toString()}`);
      }
      console.log("[EOS] sent");
      return true;
    } catch (err) {
      console.error("[EOS] failed", err);
      return false;
    }
  }

  /**
   * 手動停止／EVAS finish 只承諾目前已送出的範圍，不補送尚未播放的檔案尾端。
   * 呼叫端取得 target 後才能打 HTTP EOS。
   */
  async prepareEosForCurrentRange() {
    if (this._pendingDrain) return null;
    const generation = this._drainGeneration;
    const pcm = this._pcm16k;
    const sessionId = this._sessionId;
    const transportIdentity = this._getTransportIdentity
      ? this._getTransportIdentity()
      : null;
    if (!this._isDrainValid(generation, pcm, sessionId, transportIdentity)) return null;

    this._stopScheduler();
    if (this._audioEl && !this._audioEl.paused) this._audioEl.pause();
    const deadline = Date.now() + this._drainTimeoutMs;
    if (!await this._waitForDrainTransport(
      generation,
      pcm,
      sessionId,
      transportIdentity,
      deadline
    )) {
      this._reportDrainFailure("音檔停止位置尚未傳送完成，已取消 EOS，請重試", generation);
      return null;
    }
    if (!await this._waitForServerAcceptance(
      generation,
      pcm,
      sessionId,
      transportIdentity,
      deadline,
      this._sentSampleCount
    )) {
      this._reportDrainFailure("音檔停止位置尚未由伺服器接受，已取消 EOS，請重試", generation);
      return null;
    }

    const audioTarget = {
      sessionId,
      targetSamples: this._acceptedSampleCount,
      sampleRate: this._targetSampleRate,
    };
    if (
      audioTarget.targetSamples <= 0
      || !this._sendAudioEnd
      || this._sendAudioEnd(audioTarget) !== true
    ) {
      this._reportDrainFailure("音檔收樣確認送出失敗，已取消 EOS，請重試", generation);
      return null;
    }
    super.pause();
    return audioTarget;
  }

  _onEnded() {
    this._stopScheduler();
    if (this._pendingDrain) return this._pendingDrain;
    const generation = this._drainGeneration;
    const pcm = this._pcm16k;
    const sessionId = this._sessionId;
    const transportIdentity = this._getTransportIdentity
      ? this._getTransportIdentity()
      : null;
    const pending = this._finishEnded(generation, pcm, sessionId, transportIdentity);
    this._pendingDrain = pending;
    pending.finally(() => {
      if (this._pendingDrain === pending) this._pendingDrain = null;
    });
    return pending;
  }

}
